import {
  keepPreviousData,
  type QueryClient,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import ms from 'ms';
import { PAGINATION_PAGE_SIZE_DEFAULT } from '~/constants';

import { queryKeysCompanyAccount } from '../queryKeys';

import { fetchCompanyAccountsAdmin } from './fetchCompanyAccountsAdmin';
import {
  type CompanyAccountsResponse,
  type GetCompanyAccountsPayload,
} from './types';
import { zodCompanyAccountsQueryParamsSchema } from './validations';

/**
 * Generates the query options for the company accounts query
 * so they can be shared between the useQuery hook and the prefetching.
 */
export const getCompanyAccountsAdminQueryOptions = ({
  options = {},
  queryParameters,
}: {
  options?: Omit<
    UseQueryOptions<CompanyAccountsResponse>,
    'queryKey' | 'queryFn'
  >;
  queryParameters: Partial<GetCompanyAccountsPayload>;
}): UseQueryOptions<CompanyAccountsResponse> => {
  const validatedSearchParams =
    zodCompanyAccountsQueryParamsSchema.parse(queryParameters);

  return {
    queryFn: async () => fetchCompanyAccountsAdmin(validatedSearchParams),
    queryKey: queryKeysCompanyAccount.getAllAdmin(validatedSearchParams),
    staleTime: ms('60s'),
    ...options,
  };
};

/**
 * React Query based custom hook for getting the data for all company accounts with given query parameters.
 * Handles pagination and prefetches the next page of company accounts for better performance.
 *
 * @param queryParameters - The query parameters for fetching company accounts.
 * @param options - Additional options for the useQuery hook.
 * @returns A list of company accounts.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_company_accounts_vadmin_company_account_get
 */
export const useQueryCompanyAccountsAdmin = (
  queryParameters: Partial<GetCompanyAccountsPayload>,
  options?: Omit<
    UseQueryOptions<CompanyAccountsResponse>,
    'queryKey' | 'queryFn'
  >,
) => {
  const queryClient = useQueryClient();

  const nextPageOffset =
    (queryParameters.offset ?? 0) +
    (queryParameters.limit ?? PAGINATION_PAGE_SIZE_DEFAULT);

  void queryClient.prefetchQuery(
    getCompanyAccountsAdminQueryOptions({
      options,
      queryParameters: {
        ...queryParameters,
        offset: nextPageOffset,
      },
    }),
  );

  return useQuery<CompanyAccountsResponse>({
    ...getCompanyAccountsAdminQueryOptions({
      options,
      queryParameters,
    }),
    placeholderData: keepPreviousData,
  });
};

/**
 * TanStack Router loader function for company accounts query.
 * Allows already fetching the data when the route is visited.
 * The useQuery hook can only run when the component is mounted, which happens much later.
 *
 * @param queryClient - The query client for fetching data.
 * @return The data fetched or returned from the query.
 */
export const companyAccountsAdminLoader =
  (queryClient: QueryClient, options: Partial<GetCompanyAccountsPayload>) =>
  async () => {
    const query = getCompanyAccountsAdminQueryOptions({
      queryParameters: options,
    });
    return (
      queryClient.getQueryData(query.queryKey) ?? queryClient.fetchQuery(query)
    );
  };
